// Shared line renderer for LineChart / MultiLineChart.
//
// Lines are an SVG stretched to the box (non-scaling stroke keeps them crisp),
// but the dots are HTML elements positioned by percentage: dots drawn inside a
// stretched SVG become flat ovals, and points on the very edge (first/last
// bucket, the tallest value) were clipped in half. The plot is inset so
// nothing touches the border.
//
// A non-finite value (NaN) means "no reading in this bucket" - it breaks the
// line (a gap) instead of being drawn as 0.
import { ChartAxis } from './ChartAxis';

export interface PlotSeries {
  key: string;
  color: string;
  values: number[];
}

const INSET = 8;

// 'shared': every series on one y-axis (relative volumes stay comparable).
// 'each': every series normalised to its OWN min..max, so a signal around 7
// stays readable next to one around 41 (they differ in unit, not just size);
// real numbers stay in the legend.
export type ChartScale = 'shared' | 'each';

// DIMMED is how a non-highlighted series fades while another one is
// highlighted (hovered/tapped in the legend - see SignalActivityChart) -
// low enough to clearly recede, not so low it reads as hidden/an error.
const DIMMED = 0.2;

// How close (in px) a click needs to land to a dot to count as hitting it -
// see the single click handler below.
const TAP_HIT_RADIUS = 28;

export function LinePlot({
  series,
  height = 180,
  lineWidth = 2.2,
  dot = 8,
  scale = 'shared',
  highlightKey = null,
  selected = [],
  onSelect,
}: {
  series: PlotSeries[];
  height?: number;
  lineWidth?: number;
  dot?: number;
  scale?: ChartScale;
  // The legend's hovered/tapped series key - that line/its dots render at
  // full opacity and a touch thicker, every other series dims instead of
  // disappearing (so the shared axis/scale doesn't jump around).
  highlightKey?: string | null;
  // The pinned (clicked) dot, by series key + bucket index - rendered
  // larger with a ring so it's traceable to the detail panel's numbers.
  selected?: { key: string; index: number }[];
  onSelect?: (key: string, index: number) => void;
}) {
  const finite = series.flatMap((s) => s.values).filter(Number.isFinite);
  const max = Math.max(...finite, 1);
  const count = series[0]?.values.length ?? 0;
  const xOf = (i: number) => (count <= 1 ? 50 : (i / (count - 1)) * 100);
  // Per-series bounds for 'each'. A flat series (min === max) sits mid-height
  // rather than pinned to an edge.
  const bounds = new Map(
    series.map((s) => {
      const f = s.values.filter(Number.isFinite);
      return [s.key, { min: f.length ? Math.min(...f) : 0, max: f.length ? Math.max(...f) : 0 }] as const;
    })
  );
  const yOf = (v: number, key: string) => {
    if (scale === 'each') {
      const { min, max: hi } = bounds.get(key)!;
      return hi > min ? 100 - ((v - min) / (hi - min)) * 100 : 50;
    }
    return 100 - (v / max) * 100;
  };

  // Two series often read nearly identical values in the same bucket (two
  // channels' "Outdoor" temperature, say), so their dots land on or next to
  // each other on screen. Per-dot click handlers used to resolve to
  // whichever dot was last in the DOM (later series paint on top), not
  // whichever the user actually clicked closest to - a click meant for one
  // series kept silently selecting a different, visually-stacked one.
  // Finding the single closest dot across every series instead, from one
  // click handler on the whole plot, makes the selection match the click
  // location exactly once, regardless of how many dots overlap there.
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onSelect) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const candidates: { key: string; index: number; distSq: number }[] = [];
    for (const s of series) {
      s.values.forEach((v, i) => {
        if (!Number.isFinite(v)) return;
        const dx = (xOf(i) / 100) * rect.width - clickX;
        const dy = (yOf(v, s.key) / 100) * rect.height - clickY;
        candidates.push({ key: s.key, index: i, distSq: dx * dx + dy * dy });
      });
    }
    const best = candidates.reduce<(typeof candidates)[number] | null>(
      (min, c) => (!min || c.distSq < min.distSq ? c : min),
      null
    );
    if (best && best.distSq <= TAP_HIT_RADIUS * TAP_HIT_RADIUS) onSelect(best.key, best.index);
  };

  return (
    <ChartAxis max={max} height={height}>
      <div
        className={`absolute ${onSelect ? 'cursor-pointer' : ''}`}
        style={{ inset: INSET }}
        onClick={onSelect ? handleClick : undefined}
      >
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full overflow-visible">
          {series.map((s) => {
            // One subpath per run of consecutive readings.
            let d = '';
            let pen = false;
            s.values.forEach((v, i) => {
              if (!Number.isFinite(v)) {
                pen = false;
                return;
              }
              d += `${pen ? 'L' : 'M'}${xOf(i)},${yOf(v, s.key)} `;
              pen = true;
            });
            const isOther = highlightKey != null && s.key !== highlightKey;
            return (
              <path
                key={s.key}
                d={d}
                fill="none"
                stroke={s.color}
                strokeWidth={isOther ? lineWidth : s.key === highlightKey ? lineWidth * 1.4 : lineWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                opacity={isOther ? DIMMED : 1}
              />
            );
          })}
        </svg>
        {series.map((s) =>
          s.values.map((v, i) => {
            if (!Number.isFinite(v)) return null;
            const isSelected = selected.some((x) => x.key === s.key && x.index === i);
            return (
              // Hit-testing happens once for the whole plot (the onClick
              // above), not per dot - see handleClick's comment.
              <span
                key={`${s.key}-${i}`}
                className={`absolute rounded-full pointer-events-none ${
                  isSelected ? 'ring-2 ring-offset-1 ring-black/60 dark:ring-white/70' : ''
                }`}
                style={{
                  left: `${xOf(i)}%`,
                  top: `${yOf(v, s.key)}%`,
                  width: isSelected ? dot * 1.5 : dot,
                  height: isSelected ? dot * 1.5 : dot,
                  background: s.color,
                  transform: 'translate(-50%, -50%)',
                  opacity: highlightKey != null && s.key !== highlightKey ? DIMMED : 1,
                }}
              />
            );
          })
        )}
      </div>
    </ChartAxis>
  );
}
